import { Injectable } from '@angular/core';

import { ConvertHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, first, map, switchMap, tap } from 'rxjs/operators';
import { AddressStatus, AuditItemTypes, ObjectKeys } from '../constants';
import { Audit, BibCategory, BibQuestion, Library } from '../models';
import { Address } from '../classes/persist-address.class';
import { IPersistResult } from '../interfaces/persist-result.interface';
import { DataService } from './data.service';
import { AuditItemCategory, Person, ReadObjectsInputMain, ReadObjectsInputQueries } from '../classes';


@Injectable({
  providedIn: 'root',
})
export class AuditService {
  private maxSeqNumber = 0;
  constructor(
    private coreDataService: CoreDataService,
    private dataService: DataService,
    private persistenceService: PersistenceService
  ) { }

  public getAudit(): Audit {
    const audit = new Audit();
    return audit;
  }

  public prepareQueryToGetAuditItemsCategories(auditId: string): Observable<AuditItemCategory[]> {
    const measure = new ReadObjectsInputQueries({
      name: 'Measures',
      opath: 'BibMeasures',
      columns: ['Id', 'Title', 'Goal'],
      specialColumns: [
        this.coreDataService.createQueryColumn('ParentId', 'ResponseId'),
        this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending),
      ],
    });

    const responses = new ReadObjectsInputQueries({
      name: 'Responses',
      opath: 'BibQuestion.BibResponses',
      columns: ['Id', 'Designation', 'Score'],
      specialColumns: [this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending)],
      objectQueries: [measure],
    });

    const selectedResponseMeasure = new ReadObjectsInputQueries({
      name: 'Measures',
      opath: 'Measures',
      columns: ['Id', 'Designation', 'Goal'],
      specialColumns: [this.coreDataService.createQueryColumn('IdRefAuditItem', 'ResponseId')],
    });

    const selectedResponse = new ReadObjectsInputQueries({
      name: 'SelectedResponse',
      opath: 'SubAuditItems',
      columns: ['Id', 'Designation', 'Score', 'IdRefBibResponse'],
      objectQueries: [selectedResponseMeasure],
    });

    const questions = new ReadObjectsInputQueries({
      name: 'Questions',
      opath: 'SubAuditItems',
      columns: [
        'Id',
        'Title',
        'Designation',
        'Score',
        'MaxScore',
        'SysUserNameInsert',
        'SysUserNameUpdate',
        'Percentage',
        'IdRefBibQuestion',
      ],
      specialColumns: [
        this.coreDataService.createQueryColumn('ref:IdRefBibQuestion', 'description'),
        this.coreDataService.createQueryColumn('BibQuestion.Description', 'QuestionDescription'),
        this.coreDataService.createQueryColumn('SysDateUpdate', 'UpdatedDate'),
        this.coreDataService.createQueryColumn('SysDateInsert', 'InsertedDate'),
        this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending),
        this.coreDataService.createQueryColumn('BibQuestion.Factor', 'Factor'),
        this.coreDataService.createQueryColumn('ParentAuditItem.Designation', 'CategoryName'),
      ],

      objectQueries: [selectedResponse, responses],
    });

    const categories = new ReadObjectsInputMain({
      objectKey: ObjectKeys.AUDITITEM,
      opath: `ParentId='${auditId}' And Type LIKE 'Category'`,
      columns: [
        'Id',
        'Designation',
        'Score',
        'MaxScore',
        'SysUserNameInsert',
        'SysUserNameUpdate',
        'Percentage',
        'IdRefBibCategory',
      ],
      specialColumns: [
        this.coreDataService.createQueryColumn('SysDateInsert', 'InsertedDate'),
        this.coreDataService.createQueryColumn('SysDateUpdate', 'UpdatedDate'),
        this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending),
      ],
      objectQueries: [questions],
    });

    return this.dataService.readObjectData<AuditItemCategory>(categories);
  }

  public prepareQueryToGetAudit(auditId: string): Observable<Audit[]> {
    return this.dataService.readObjectData<Audit>({
      objectKey: ObjectKeys.AUDIT,
      opath: `Id='${auditId}'`,
      columns: ['Id', 'Score', 'MaxScore', 'Percentage', 'Designation']
    })
  }

  public getCategoriesAndQuestionsByLibrary(libraryOpath: string): Observable<Library[]> {
    const libraryQuery: Query = new Query();
    const libraryQueryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
    ];
    libraryQuery.ObjectType = ObjectKeys.LIBRARY;
    libraryQuery.Columns = libraryQueryColumns;
    libraryQuery.OPath = libraryOpath;

    const subQueryCategories: Query = new Query();
    subQueryCategories.Name = 'Categories';
    subQueryCategories.OPath = 'BibCategories';
    const subQueryCategoryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending)
    ];
    subQueryCategories.Columns = subQueryCategoryColumns;

    const subQueryQuestions: Query = new Query();
    subQueryQuestions.Name = 'Questions';
    subQueryQuestions.OPath = 'BibQuestions';
    const subQueryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('Title', 'Title'),
      this.coreDataService.createQueryColumn('Score', 'Score'),
      this.coreDataService.createQueryColumn('Factor', 'Factor'),
      this.coreDataService.createQueryColumn('SortNr', 'SortNr'),
      this.coreDataService.createQueryColumn('Max(BibResponses.ToFloat(Score))', 'MaxScore'),
    ];
    subQueryQuestions.Columns = subQueryColumns;

    subQueryCategories.ObjectQueries = [subQueryQuestions];

    libraryQuery.ObjectQueries = [subQueryCategories];

    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [libraryQuery];

    return this.coreDataService
      .executeReadObjectsQuery(queryDocument)
      .pipe(map((data) => ConvertHelper.arrayBufferToObjectArray(data)));
  }

  public getCategorizingQuestions(categorizingQuestionsId: string): Observable<BibCategory[]> {
    const subQueryCategories: Query = new Query();
    const subQueryCategoryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('SortNr', 'SortNr'),
    ];
    subQueryCategories.ObjectType = ObjectKeys.BIBCATEGORY;
    subQueryCategories.Columns = subQueryCategoryColumns;
    subQueryCategories.OPath = `Id='${categorizingQuestionsId}'`;
    subQueryCategories.Columns = subQueryCategoryColumns;
    const subQueryQuestions: Query = new Query();
    subQueryQuestions.Name = 'Questions';
    subQueryQuestions.OPath = 'BibQuestions';
    const subQueryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('Title', 'Title'),
      this.coreDataService.createQueryColumn('Score', 'Score'),
      this.coreDataService.createQueryColumn('Factor', 'Factor'),
      this.coreDataService.createQueryColumn('SortNr', 'SortNr'),
      this.coreDataService.createQueryColumn('Max(BibResponses.ToFloat(Score))', 'MaxScore'),
    ];
    subQueryQuestions.Columns = subQueryColumns;
    subQueryCategories.ObjectQueries = [subQueryQuestions];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [subQueryCategories];
    return this.coreDataService
      .executeReadObjectsQuery(queryDocument)
      .pipe(map((data) => ConvertHelper.arrayBufferToObjectArray(data)));
  }

  public prepareQueryToGetAudits(companyId?): QueryObjectsModel {
    const mainQuery: Query = new Query();
    const mainQueryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('AuditDate', 'AuditDate'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('Percentage', 'Percentage'),
      this.coreDataService.createQueryColumn(
        "Exists(Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id])",
        'IsFavorite'
      ),
      this.coreDataService.createQueryColumn(
        "Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id].Id",
        'BoxDetailId'
      ),
    ];
    const otherColumns = [
      this.coreDataService.createQueryColumn('IdRefLibrary', 'LibraryId'),
      this.coreDataService.createQueryColumn('ref:ParentId', 'Company'),
      this.coreDataService.createQueryColumn('Score', 'Score'),
      this.coreDataService.createQueryColumn('MaxScore', 'MaxScore'),
      this.coreDataService.createQueryColumn('ParentId', 'CompanyId'),
    ];
    if (!companyId) {
      mainQueryColumns.push(...otherColumns);
    }
    mainQuery.ObjectType = ObjectKeys.AUDIT;
    mainQuery.Columns = mainQueryColumns;
    if (companyId) {
      mainQuery.OPath = `ParentId='${companyId}'`;
    }
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];

    return queryDocument;
  }

  public insertCompany(company: Address): Observable<IPersistResult> {
    return this.dataService.insertObject(ObjectKeys.ADDRESS, company);
  }

  private searchPotentialClientStatus(
    addressStatus: string = AddressStatus.PotentialCustomer
  ): Observable<{ Id: string; Value: string; Description: string }[]> {
    return this.dataService.getPickListItemsByPickListName('Kundenstatus', 1031, `Description Like '${addressStatus}'`);
  }

  public persistCompany(
    company: Address,
    addressStatus: string = AddressStatus.PotentialCustomer
  ): Observable<IPersistResult> {
    return this.searchPotentialClientStatus(addressStatus).pipe(
      first(),
      catchError((err) => {
        return of([{ Value: 'not Found' }]);
      }),
      exhaustMap((potentialClientStatus) => {
        company.Status = potentialClientStatus[0].Value;
        company.MatchCode = this.getAbreviation(company.Name);
        return this.insertCompany(company);
      })
    );
  }

  private getAbreviation(companyName: string): string {
    if (!companyName) return '';

    return companyName
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  public persistAudit(
    auditDesignation: string,
    auditDate: string,
    companyId: string,
    libraryId: string,
    userId?: string
  ): Observable<IPersistResult> {
    const auditPersistQuery: TargetObjectData = new TargetObjectData();
    auditPersistQuery.Mode = PersistMode.Insert;
    auditPersistQuery.ObjectKey = new ObjectKey();
    auditPersistQuery.ObjectKey.ObjectType = ObjectKeys.AUDIT;
    const auditColumns: TargetColumnValue[] = [
      { Name: 'Designation', Value: auditDesignation },
      {
        Name: 'AuditDate',
        Value: auditDate,
      },
      { Name: 'ParentId', Value: companyId },
      { Name: 'IdRefLibrary', Value: libraryId },
      { Name: 'Score', Value: '0' },
      { Name: 'Percentage', Value: '0' },
      { Name: 'IdRefAuditPerson', Value: userId },
    ];
    auditPersistQuery.TargetColumns = auditColumns;
    const query: PersistObjectModel = new PersistObjectModel();
    query.Object = auditPersistQuery;
    return this.persistenceService
      .executePersistObjectQuery(query)
      .pipe(map((persistedCompany) => ConvertHelper.arrayBufferToObject(persistedCompany)));
  }

  public updateAudit(auditId: string, audit: Audit) {
    return this.dataService.updateObject<Audit>(ObjectKeys.AUDIT, auditId, audit);
  }

  public generateAuditItemCategories(bibCategories: BibCategory[], addedAuditId: string): Observable<any> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    let auditItemCategoryPersistQuery: TargetObjectData[][] = [[]];

    bibCategories?.forEach((category) => {
      let auditItemPersistObjectData_Category: TargetObjectData = new TargetObjectData();
      auditItemPersistObjectData_Category.Mode = PersistMode.Insert;
      auditItemPersistObjectData_Category.ObjectKey = new ObjectKey();
      auditItemPersistObjectData_Category.ObjectKey.ObjectType = ObjectKeys.AUDITITEM;

      const auditItemColumnsCategory: TargetColumnValue[] = [
        { Name: 'IdRefBibCategory', Value: category.Id },
        { Name: 'ParentId', Value: addedAuditId },
        { Name: 'Score', Value: '0' },
        {
          Name: 'MaxScore',
          Value: this.returnCategoryMaxScore(category.Questions)?.toFixed(2).toString(),
        },
        { Name: 'Percentage', Value: '0' },

        { Name: 'Type', Value: AuditItemTypes.Category },
        { Name: 'SeqNr', Value: (this.maxSeqNumber + 1).toString() },
        { Name: 'Designation', Value: category.Designation },
        { Name: 'SortNr', Value: category.SortNr },
      ];

      auditItemPersistObjectData_Category.TargetColumns = auditItemColumnsCategory;
      auditItemCategoryPersistQuery[0].push(auditItemPersistObjectData_Category);
      this.maxSeqNumber = this.maxSeqNumber + 1;
    });

    query.Objects = auditItemCategoryPersistQuery;

    return this.persistenceService.executePersistObjectsQuery(query);
  }

  public returnCategoryMaxScore(questions: BibQuestion[]): number {
    return questions?.length > 0
      ? questions?.reduce(
        (maxScore, question) => (maxScore = maxScore + question.MaxScore * (question.Factor ? question.Factor : 1)),
        0
      )
      : 0;
  }

  public generateAuditItemQuestions(
    bibCategories: BibCategory[],
    addedCategoryAudits: any[],
    addedAuditId: string
  ): Observable<any> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    let auditItemPersistQuery: TargetObjectData[][] = [[]];

    bibCategories.forEach((category, categoryIndex) => {
      category.Questions?.forEach((question) => {
        const auditCategory = addedCategoryAudits[categoryIndex];
        let auditItemPersistObjectData_Question: TargetObjectData = new TargetObjectData();
        auditItemPersistObjectData_Question.Mode = PersistMode.Insert;
        auditItemPersistObjectData_Question.ObjectKey = new ObjectKey();
        auditItemPersistObjectData_Question.ObjectKey.ObjectType = ObjectKeys.AUDITITEM;

        const auditItemColumnsQuestion: TargetColumnValue[] = [
          { Name: 'ParentId', Value: addedAuditId },
          { Name: 'IdRefBibQuestion', Value: question.Id },
          { Name: 'Title', Value: question.Title },
          { Name: 'Designation', Value: question.Designation },
          { Name: 'Score', Value: '0' },
          { Name: 'MaxScore', Value: question.MaxScore },
          { Name: 'Percentage', Value: '0' },
          { Name: 'Type', Value: AuditItemTypes.Question },
          { Name: 'SeqNr', Value: (this.maxSeqNumber + 1).toString() },
          { Name: 'IdRefParentAuditItem', Value: auditCategory.Id },
          { Name: 'SortNr', Value: question.SortNr },
        ];

        auditItemPersistObjectData_Question.TargetColumns = auditItemColumnsQuestion;
        auditItemPersistQuery[0].push(auditItemPersistObjectData_Question);
        this.maxSeqNumber = this.maxSeqNumber + 1;
      });
    });

    query.Objects = auditItemPersistQuery;

    return this.persistenceService.executePersistObjectsQuery(query);
  }

  public QueryToGetAuditMaxScore(auditId): Observable<any> {
    const query: Query = new Query();
    const queryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn("Sum(AuditItems[Type = 'Category'].MaxScore)", 'MaxScore'),
    ];
    query.ObjectType = ObjectKeys.AUDIT;
    query.Columns = queryColumns;
    query.OPath = `Id='${auditId}'`;
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [query];

    return this.coreDataService.executeReadObjectsQuery(queryDocument);
  }

  public updateAuditMaxScore(auditId, maxScore): Observable<any> {
    const updateAuditMaxScoreQueryData: TargetObjectData = new TargetObjectData();
    updateAuditMaxScoreQueryData.Mode = PersistMode.Update;
    updateAuditMaxScoreQueryData.ObjectKey = new ObjectKey();
    updateAuditMaxScoreQueryData.ObjectKey.ObjectType = ObjectKeys.AUDIT;
    updateAuditMaxScoreQueryData.ObjectKey.Id = auditId;

    const responseAuditItemColumns: TargetColumnValue[] = [
      { Name: 'MaxScore', Value: this.floatToInt(maxScore).toString() },
    ];

    updateAuditMaxScoreQueryData.TargetColumns = responseAuditItemColumns;
    const updateAuditMaxScoreQuery: PersistObjectModel = new PersistObjectModel();
    updateAuditMaxScoreQuery.Object = updateAuditMaxScoreQueryData;

    return this.persistenceService.executePersistObjectQuery(updateAuditMaxScoreQuery);
  }

  public floatToInt(value): number {
    if (value != null) {
      return value | 0;
    } else {
      return 0;
    }
  }

  public getScoreInPercent(score: number, maxScore: number): number {
    let scoreInPercent = maxScore != 0 ? (score / maxScore) * 100 : 0;
    return Math.round(scoreInPercent * 100) / 100 < 0 ? 0 : Math.round(scoreInPercent * 100) / 100;
  }

  public getGaugeMsg(score: number): string {
    if (score >= 60 && score <= 100) {
      return 'Herzlichen Glückwunsch zur Erreichung des Experten-Levels in der digitalen Reife! Unsere Zusammenarbeit kann zukunftsweisende Technologien wie KI oder Automatisierung in Ihre Softwarelösungen bringen. Wir bleiben auf dem neuesten Stand der Branchenstandards und arbeiten gemeinsam an kontinuierlicher Innovation und Verbesserung in Ihrem fortgeschrittenen digitalen Ökosystem.';
    } else if (score >= 44.5 && score <= 59.99) {
      return 'Beeindruckende Arbeit bei der Weiterentwicklung Ihrer Digitalisierungsbemühungen! Unsere Softwarelösungen können nahtlos über alle  Abteilungen hinweg integriert werden.';
    } else if (score >= 24.85 && score < 44.5) {
      return 'Mit Ihrem Fortschritt in der Digitalisierung können unsere Softwareentwicklungsdienste Ihr Unternehmen weiterbringen. Wir entwickeln für Sie ein passendes Konzept und setzen es um, um Ihre Prozesse effizienter zu gestalten und optimal auf Ihre individuellen Anforderungen auszurichten.';
    } else if (score >= 0 && score < 24.84) {
      return 'Als Sie Ihre Reise in die Digitalisierung beginnen, sind unsere Softwarelösungen darauf ausgelegt, Ihre Prozesse zu vereinfachen. Wir können eng mit Ihnen zusammenarbeiten, um Ihre Anforderungen zu verstehen und maßgeschneiderte Lösungen zu entwickeln, ohne dass umfangreiches Mitarbeiterschulungen erforderlich sind.';
    } else return '';
  }

  public InsertGuestResultInComment(auditId: string, result: string): Observable<IPersistResult> {
    const identifier = new Date().getTime().toString();
    const commet = {
      ParentId: auditId,
      Identifier: identifier,
      Comment: result,
      ParentObjectType: ObjectKeys.AUDIT,
    };

    return this.dataService.insertObject(ObjectKeys.COMMENT, commet);
  }

  public persistAuditWithCategoriesAndQuestions(
    auditDesignation: string,
    auditDate: string,
    companyId: string,
    libraryId: string,
    bibCategories: BibCategory[],
    categorizingQuestions?: BibCategory,
    userId?: string
  ): Observable<IPersistResult> {
    this.maxSeqNumber = 0;

    return this.persistAudit(auditDesignation, auditDate, companyId, libraryId, userId).pipe(
      tap((audit) => {
        if (categorizingQuestions) {
          bibCategories.push(categorizingQuestions);
        }
      }),
      switchMap((audit) =>
        this.generateAuditItemCategories(bibCategories, audit.Id).pipe(
          switchMap((categories) =>
            this.generateAuditItemQuestions(bibCategories, categories[0], audit.Id).pipe(
              switchMap((questions) =>
                this.QueryToGetAuditMaxScore(audit.Id).pipe(
                  switchMap((auditMaxScore) => this.updateAuditMaxScore(audit.Id, auditMaxScore[0].MaxScore))
                )
              )
            )
          )
        )
      ),
      map((audit) => audit)
    );
  }

  public getAuditQuery(withPerson = true, companyIdToExeclude?: string): Observable<Audit[]> {
    const auditQuery = new ReadObjectsInputMain({
      objectKey: ObjectKeys.AUDIT,
      columns: ['Id', 'AuditDate', 'Designation', 'Percentage', 'Score', 'MaxScore'],
      specialColumns: [
        this.coreDataService.createQueryColumn(
          "Exists(Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id])",
          'IsFavorite'
        ),
        this.coreDataService.createQueryColumn(
          "Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id].Id",
          'BoxDetailId'
        ),
        this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
        this.coreDataService.createQueryColumn('ref:ParentId', 'Company'),
        this.coreDataService.createQueryColumn('IdRefLibrary', 'LibraryId'),
      ],
    });

    if (companyIdToExeclude) auditQuery.opath = `ParentId <> '${companyIdToExeclude}'`;

    if (withPerson) {
      const filterOutWithoutPerson = 'Not(IsNull(IdRefAuditPerson))';
      auditQuery.opath = companyIdToExeclude ? `${auditQuery.opath} And ${filterOutWithoutPerson}` : filterOutWithoutPerson;
      auditQuery.specialColumns.push(
        this.coreDataService.createQueryColumn('Person.FirstName', 'PersonFirstName'),
        this.coreDataService.createQueryColumn('Person.LastName', 'PersonLastName'),
        this.coreDataService.createQueryColumn('Person.Email', 'PersonEmail'),
        this.coreDataService.createQueryColumn('Person.OfficePhone', 'PersonOfficePhone'),
        this.coreDataService.createQueryColumn('Person.SupportObservation', 'PersonSupportObservation'),
        this.coreDataService.createQueryColumn('Person.Reserve1', 'PersonRecommender')
      );
    }

    return this.dataService.readObjectData<Audit>(auditQuery);
  }

  public persistPerson(user: Person, companyId: string): Observable<IPersistResult> {
    const personPersistQuery: TargetObjectData = new TargetObjectData();
    personPersistQuery.ObjectKey = new ObjectKey();
    personPersistQuery.ObjectKey.ObjectType = ObjectKeys.Person;

    personPersistQuery.Mode = PersistMode.Insert;

    const personColumns: TargetColumnValue[] = [
      { Name: 'ParentId', Value: companyId },
      { Name: 'FirstName', Value: user.FirstName },
      { Name: 'LastName', Value: user.LastName },
      { Name: 'EMail', Value: user.EMail },
      { Name: 'OfficePhone', Value: user.OfficePhone },
      { Name: 'Active', Value: false },
      { Name: 'Reserve1', Value: user.Recommender },
    ];
    personPersistQuery.TargetColumns = personColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = personPersistQuery;

    return this.persistenceService
      .executePersistObjectQuery(persistObject)
      .pipe(map((persistedUser) => ConvertHelper.arrayBufferToObject(persistedUser)));
  }

  public formatAuditDesignation(firstWord = 'Digitalisierungscheck'): string {
    const now = new Date();

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };

    const date = now.toLocaleDateString('de-DE', options);

    return `${firstWord}-${date}_${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;
  }

  public getAuditDesignation(opath: string): Observable<Audit[]> {
    return this.dataService.readObjectData<Audit>({
      objectKey: ObjectKeys.AUDIT,
      columns: ['Designation'],
      opath: opath
    })
  }

  public getCategories(opath: string): Observable<BibCategory[]> {
    return this.dataService.readObjectData<BibCategory>({
      objectKey: ObjectKeys.BIBCATEGORY,
      columns: ['Designation'],
      specialColumns: [this.coreDataService.createQueryColumn('SortNr', 'SortNr', QueryColumnSortOrder.Ascending)],
      opath: opath
    })
  }

}
